/** Australia (TGA) IVD submission pathways — the dropdown options shown when a
 *  user picks AU IVD at dossier dispatch time. Mirrors `au-submission-pathways.ts`
 *  (the AU MD file) in shape so the dispatch form plugs it in with a key swap.
 *
 *  FIRST DRAFT — labels pending regulatory review. These reflect standard TGA
 *  IVD filing classifications under the Therapeutic Goods (Medical Devices)
 *  Regulations 2002 (TG(MD)Regs), Schedule 2A, as of 2026 but have not yet been
 *  reviewed by a qualified Australian regulatory consultant. Do not rely on these
 *  labels as authoritative regulatory guidance.
 *
 *  ## IVD Medical Devices — ARTG inclusion via TGA Conformity Assessment
 *
 *  TGA uses a risk-based IVD classification scheme under Schedule 2A of the
 *  TG(MD)Regs 2002. Class 1 IVDs are the lowest risk (self-declared; no
 *  conformity-assessment dossier required — out of scope for this engine).
 *  Classes 2, 3, and 4 require a TGA conformity assessment (or Manufacturer's
 *  Evidence acceptance) leading to ARTG inclusion under ss 41FC/41FD/41FF of
 *  the Therapeutic Goods Act 1989, and compliance with Essential Principle 15
 *  (performance evaluation for IVDs).
 *
 *  Slug values MUST match the backend `_UI_IVD_ALIASES` in
 *  `app/dossier/australia_ivd/dispatch_policy.py` exactly:
 *    "au-ivd-class-2" → "2"
 *    "au-ivd-class-3" → "3"
 *    "au-ivd-class-4" → "4"
 */

import type { Product } from '@/features/product/types';
import type { SubmissionGroup } from './us-submission-pathways';

export const AU_IVD_SUBMISSION_PATHWAYS: SubmissionGroup[] = [
  {
    productType: 'device',
    label: 'IVD Medical Device — TGA / ARTG (TG(MD)Regs 2002, Schedule 2A)',
    options: [
      {
        value: 'au-ivd-class-2',
        label: 'Class 2 — TGA conformity assessment (Essential Principle 15, low–moderate risk IVD)',
      },
      {
        value: 'au-ivd-class-3',
        label: 'Class 3 — TGA conformity assessment (Essential Principle 15, moderate–high risk IVD)',
      },
      {
        value: 'au-ivd-class-4',
        label: 'Class 4 — TGA conformity assessment (Essential Principle 15, highest risk IVD)',
      },
    ],
  },
];

/** Pre-fill the picker with the most-common IVD pathway per product type. */
export const AU_IVD_DEFAULT_BY_TYPE: Record<Product['product_type'], string> = {
  device:      'au-ivd-class-2',
  drug:        'au-ivd-class-2',
  biologic:    'au-ivd-class-2',
  combination: 'au-ivd-class-2',
};
